import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import produce, { current } from 'immer';
import UAParser from 'ua-parser-js';
import useBitrateTestUDP from './useBitrateTest/useBitrateTestUDP';
import useBitrateTestTCP from './useBitrateTest/useBitrateTestTCP';
import { VideoInputTest, MediaConnectionBitrateTest, AudioInputTest, AudioOutputTest } from '@twilio/rtc-diagnostics';
import useSipConnectivityTest, { SipReport } from './useSipConnectivityTest/useSipConnectivityTest';
import useCodecsTest, { CodecReport } from './useCodecsTest/useCodecsTest';
import { SpeedReport, SpeedResult, useSpeedTest } from './useSpeedTest/useSpeedTest';

export enum ActivePane {
  GetStarted,
  DeviceCheck,
  DeviceError,
  CameraTest,
  SpeakersTest,
  AudioTest,
  BrowserTest,
  Connectivity,
  CodecsTest,
  Quality,
  Results,
}

export interface stateType {
  activePane: ActivePane;
  videoGranted: boolean;
  audioGranted: boolean;
  deviceError: null | Error;
  videoInputTestReport: null | VideoInputTest.Report;
  audioInputTestReport: null | AudioInputTest.Report;
  audioOutputTestReport: null | AudioOutputTest.Report;
  deviceTestIsNotFinished: boolean;
  downButtonDisabled: boolean;
  bitrateTestUDP: {
    bitrateUDP: null | number;
    reportUDP: null | MediaConnectionBitrateTest.Report;
    errorUDP: null | Error;
  };
  bitrateTestInProgressUDP: boolean;
  bitrateTestFinishedUDP: boolean;
  bitrateTestTCP: {
    enabled: boolean;
    bitrateTCP: null | number;
    reportTCP: null | MediaConnectionBitrateTest.Report;
    errorTCP: null | Error;
  };
  bitrateTestInProgressTCP: boolean;
  bitrateTestFinishedTCP: boolean;
  sipConnectivityUSTestConnected: boolean;
  sipConnectivityUSTestDisconnected: boolean;
  sipConnectivityUSTestReport: null | SipReport;
  sipConnectivitySATestConnected: boolean;
  sipConnectivitySATestDisconnected: boolean;
  sipConnectivitySATestReport: null | SipReport;
  audioCodecTestSuccessful: boolean;
  audioCodecTestFailed: boolean;
  audioCodecTestReport: null | CodecReport[];
  videoCodecTestSuccessful: boolean;
  videoCodecTestFailed: boolean;
  videoCodecTestReport: null | CodecReport[];
  downloadSpeed: SpeedResult[];
  downloadSpeedResult: null | number;
  uploadSpeed: SpeedResult[];
  uploadSpeedResult: null | number;
  rttResult: null | number;
  jitterResult: null | number;
  errorSpeed: null | SpeedReport;
  historyResults: any[];
}

export type ACTIONTYPE =
  | { type: 'set-active-pane'; newActivePane: ActivePane }
  | { type: 'next-pane' }
  | { type: 'previous-pane' }
  | { type: 'set-devices'; devices: MediaDeviceInfo[] }
  | { type: 'set-device-error'; error: Error }
  | { type: 'set-video-test-report'; report: VideoInputTest.Report }
  | { type: 'set-audio-input-test-report'; report: AudioInputTest.Report }
  | { type: 'set-audio-output-test-report'; report: AudioOutputTest.Report }
  | { type: 'set-device-test-not-finished'; state: boolean }
  | { type: 'udp-set-bitrate'; bitrate: number }
  | { type: 'udp-set-bitrate-test-error'; error: Error }
  | { type: 'udp-set-bitrate-test-report'; report: MediaConnectionBitrateTest.Report }
  | { type: 'udp-bitrate-test-started' }
  | { type: 'udp-bitrate-test-finished' }
  | { type: 'tcp-set-bitrate-test-enabled' }
  | { type: 'tcp-set-bitrate-test-disabled' }
  | { type: 'tcp-set-bitrate'; bitrate: number }
  | { type: 'tcp-set-bitrate-test-error'; error: Error }
  | { type: 'tcp-set-bitrate-test-report'; report: MediaConnectionBitrateTest.Report }
  | { type: 'tcp-bitrate-test-started' }
  | { type: 'tcp-bitrate-test-finished' }
  | { type: 'sip-connectivity-us-test-connected' }
  | { type: 'sip-connectivity-us-test-disconnected' }
  | { type: 'sip-connectivity-us-test-report'; report: SipReport }
  | { type: 'sip-connectivity-sa-test-connected' }
  | { type: 'sip-connectivity-sa-test-disconnected' }
  | { type: 'sip-connectivity-sa-test-report'; report: SipReport }
  | { type: 'audio-codecs-test-successful' }
  | { type: 'audio-codecs-test-failed' }
  | { type: 'audio-codecs-test-report'; report: CodecReport[] }
  | { type: 'video-codecs-test-successful' }
  | { type: 'video-codecs-test-failed' }
  | { type: 'video-codecs-test-report'; report: CodecReport[] }
  | { type: 'download-speed'; speed: SpeedResult }
  | { type: 'download-speed-result'; speed: number }
  | { type: 'clean-speed'; speed: SpeedResult }
  | { type: 'upload-speed'; speed: SpeedResult }
  | { type: 'upload-speed-result'; speed: number }
  | { type: 'rtt-result'; result: number }
  | { type: 'jitter-result'; result: number }
  | { type: 'error-speed'; report: SpeedReport }
  | { type: 'history-results'; result: any[] };

type AppStateContextType = {
  state: stateType;
  dispatch: React.Dispatch<ACTIONTYPE>;
  nextPane: () => void;
  userAgentInfo: UAParser.IResult;
  downloadFinalTestResults: () => void;
};

export const initialState = {
  activePane: ActivePane.GetStarted,
  videoGranted: false,
  audioGranted: false,
  deviceError: null,
  preflightTest: {
    progress: null,
    report: null,
    error: null,
    tokenError: null,
    signalingGatewayReachable: false,
    turnServersReachable: false,
  },
  videoInputTestReport: null,
  audioInputTestReport: null,
  audioOutputTestReport: null,
  downButtonDisabled: true,
  deviceTestIsNotFinished: false,
  preflightTestInProgress: false,
  preflightTestFinished: false,
  bitrateTestUDP: {
    bitrateUDP: null,
    reportUDP: null,
    errorUDP: null,
  },
  bitrateTestInProgressUDP: false,
  bitrateTestFinishedUDP: false,
  bitrateTestTCP: {
    enabled: false,
    bitrateTCP: null,
    reportTCP: null,
    errorTCP: null,
  },
  bitrateTestInProgressTCP: false,
  bitrateTestFinishedTCP: false,
  sipConnectivityUSTestConnected: false,
  sipConnectivityUSTestDisconnected: false,
  sipConnectivityUSTestReport: null,
  sipConnectivitySATestConnected: false,
  sipConnectivitySATestDisconnected: false,
  sipConnectivitySATestReport: null,
  audioCodecTestSuccessful: false,
  audioCodecTestFailed: false,
  audioCodecTestReport: null,
  videoCodecTestSuccessful: false,
  videoCodecTestFailed: false,
  videoCodecTestReport: null,
  downloadSpeed: [],
  uploadSpeed: [],
  downloadSpeedResult: null,
  uploadSpeedResult: null,
  rttResult: null,
  jitterResult: null,
  errorSpeed: null,
  historyResults: [],
};

export const AppStateContext = createContext<AppStateContextType>(null!);

export function useAppStateContext() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppStateContext must be used within an AppStateProvider');
  }
  return context;
}

// helper function for determining whether to disable the down arrow button:
export const isDownButtonDisabled = (currentState: stateType) => {
  const { activePane, audioInputTestReport, audioOutputTestReport, videoInputTestReport } = currentState;

  const deviceTestErrors =
    !!audioInputTestReport?.errors.length ||
    !!audioOutputTestReport?.errors.length ||
    !!videoInputTestReport?.errors.length;

  const onDeviceCheck = activePane === ActivePane.DeviceCheck || activePane === ActivePane.DeviceError;

  return activePane === ActivePane.GetStarted || !ActivePane[activePane + 1] || deviceTestErrors || onDeviceCheck;
};

/**
 * This reducer function helps with the logic used to decide the activePane,
 * as well as which panes are to be shown to the user, and when.
 */

export const appStateReducer = produce((draft: stateType, action: ACTIONTYPE) => {
  switch (action.type) {
    case 'set-active-pane':
      draft.activePane = action.newActivePane;
      break;

    case 'next-pane':
      switch (draft.activePane) {
        case ActivePane.GetStarted:
          if (draft.audioGranted && draft.videoGranted) {
            draft.activePane = ActivePane.CameraTest;
          } else {
            draft.activePane = ActivePane.DeviceCheck;
          }
          break;
        case ActivePane.DeviceCheck:
          if (draft.deviceError) {
            draft.activePane = ActivePane.DeviceError;
          } else {
            draft.activePane = ActivePane.CameraTest;
          }
          break;
        default:
          draft.activePane++;
          break;
      }
      break;

    case 'previous-pane':
      switch (draft.activePane) {
        case ActivePane.CameraTest:
          if (draft.audioGranted && draft.videoGranted) {
            draft.activePane = ActivePane.GetStarted;
          } else {
            draft.activePane = ActivePane.DeviceCheck;
          }
          break;
        default:
          draft.activePane--;
          break;
      }
      break;

    case 'set-devices':
      draft.audioGranted = action.devices.filter((d) => d.kind === 'audioinput').every((d) => d.label);
      draft.videoGranted = action.devices.filter((d) => d.kind === 'videoinput').every((d) => d.label);
      break;

    case 'set-device-error':
      draft.deviceError = action.error;
      draft.activePane = ActivePane.DeviceError;
      break;

    case 'set-video-test-report':
      draft.videoInputTestReport = action.report;
      break;

    case 'set-audio-input-test-report':
      draft.audioInputTestReport = action.report;
      break;

    case 'set-audio-output-test-report':
      draft.audioOutputTestReport = action.report;
      break;

    case 'set-device-test-not-finished':
      draft.deviceTestIsNotFinished = action.state;
      break;

    case 'udp-set-bitrate':
      draft.bitrateTestUDP.bitrateUDP = action.bitrate;
      break;

    case 'udp-set-bitrate-test-report':
      draft.bitrateTestUDP.reportUDP = action.report;
      break;

    case 'udp-set-bitrate-test-error':
      draft.bitrateTestUDP.errorUDP = action.error;
      break;

    case 'udp-bitrate-test-started':
      draft.bitrateTestInProgressUDP = true;
      draft.bitrateTestFinishedUDP = false;
      break;

    case 'udp-bitrate-test-finished':
      draft.bitrateTestFinishedUDP = true;
      draft.bitrateTestInProgressUDP = false;
      break;

    case 'tcp-set-bitrate-test-enabled':
      draft.bitrateTestTCP.enabled = true;
      break;

    case 'tcp-set-bitrate-test-disabled':
      draft.bitrateTestTCP.enabled = false;
      break;

    case 'tcp-set-bitrate':
      draft.bitrateTestTCP.bitrateTCP = action.bitrate;
      break;

    case 'tcp-set-bitrate-test-report':
      draft.bitrateTestTCP.reportTCP = action.report;
      break;

    case 'tcp-set-bitrate-test-error':
      draft.bitrateTestTCP.errorTCP = action.error;
      break;

    case 'tcp-bitrate-test-started':
      draft.bitrateTestInProgressTCP = true;
      draft.bitrateTestFinishedTCP = false;
      break;

    case 'tcp-bitrate-test-finished':
      draft.bitrateTestFinishedTCP = true;
      draft.bitrateTestInProgressTCP = false;
      break;

    case 'sip-connectivity-us-test-disconnected':
      draft.sipConnectivityUSTestDisconnected = true;
      break;

    case 'sip-connectivity-us-test-connected':
      draft.sipConnectivityUSTestConnected = true;
      break;

    case 'sip-connectivity-us-test-report':
      draft.sipConnectivityUSTestReport = action.report;
      break;

    case 'sip-connectivity-sa-test-disconnected':
      draft.sipConnectivitySATestDisconnected = true;
      break;

    case 'sip-connectivity-sa-test-connected':
      draft.sipConnectivitySATestConnected = true;
      break;

    case 'sip-connectivity-sa-test-report':
      draft.sipConnectivitySATestReport = action.report;
      break;

    case 'audio-codecs-test-successful':
      draft.audioCodecTestSuccessful = true;
      break;

    case 'audio-codecs-test-failed':
      draft.audioCodecTestFailed = true;
      break;

    case 'audio-codecs-test-report':
      draft.audioCodecTestReport = action.report;
      break;

    case 'video-codecs-test-successful':
      draft.videoCodecTestSuccessful = true;
      break;

    case 'video-codecs-test-failed':
      draft.videoCodecTestFailed = true;
      break;

    case 'video-codecs-test-report':
      draft.videoCodecTestReport = action.report;
      break;

    case 'download-speed':
      draft.downloadSpeed.push(action.speed);
      break;

    case 'download-speed-result':
      draft.downloadSpeedResult = action.speed;
      break;

    case 'upload-speed-result':
      draft.uploadSpeedResult = action.speed;
      break;

    case 'clean-speed':
      draft.downloadSpeed = [];
      draft.uploadSpeed = [];
      draft.uploadSpeedResult = null;
      draft.downloadSpeedResult = null;
      draft.jitterResult = null;
      draft.rttResult = null;
      break;

    case 'upload-speed':
      draft.uploadSpeed.push(action.speed);
      break;

    case 'rtt-result':
      draft.rttResult = action.result;
      break;

    case 'jitter-result':
      draft.jitterResult = action.result;
      break;

    case 'error-speed':
      draft.errorSpeed = action.report;
      break;

    case 'history-results':
      draft.historyResults = action.result;
      break;
  }

  const currentState = current(draft);

  draft.downButtonDisabled = isDownButtonDisabled(currentState);
});

export const AppStateProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState);
  var startBitrateTestUDP = useBitrateTestUDP(dispatch);
  var startBitrateTestTCP = useBitrateTestTCP(dispatch);
  var startSipConnectivityTest = useSipConnectivityTest(dispatch);
  var startCodecsTest = useCodecsTest(dispatch);
  var startSpeedTest = useSpeedTest(dispatch);

  const userAgentParser = new UAParser();
  const userAgentInfo = userAgentParser.getResult();

  const downloadFinalTestResults = () => {
    const maxBitrateTCP = state.bitrateTestTCP.reportTCP?.values
      ? Math.max(...state.bitrateTestTCP.reportTCP.values)
      : 0;
    const maxBitrateUDP = state.bitrateTestUDP.reportUDP?.values
      ? Math.max(...state.bitrateTestUDP.reportUDP.values)
      : 0;

    const finalTestResults = {
      audioTestResults: { inputTest: state.audioInputTestReport, outputTest: state.audioOutputTestReport },
      TCPBitrateTestResults: state.bitrateTestTCP.enabled ? { maxBitrateTCP, ...state.bitrateTestTCP.reportTCP } : null,
      UDPBitrateTestResults: { maxBitrateUDP, ...state.bitrateTestUDP.reportUDP },
      browserInformation: userAgentInfo,
      videoTestResults: state.videoInputTestReport,
      connectivityResults: {
        sipUSReportTestResults: state.sipConnectivityUSTestReport,
        sipSAReportTestResults: state.sipConnectivitySATestReport,
      },
      audioCodecsTestResult: state.audioCodecTestReport,
      videoCodecsTestResult: state.videoCodecTestReport,
    };

    const link = document.createElement('a');
    link.download = 'test_results.json';
    link.href = URL.createObjectURL(
      new Blob([JSON.stringify(finalTestResults, null, 2)], {
        type: 'text/plain',
      })
    );
    link.click();
    return finalTestResults;
  };

  const nextPane = useCallback(async () => {
    switch (state.activePane) {
      case ActivePane.GetStarted:
        startSipConnectivityTest();
        startCodecsTest();
        startBitrateTestUDP();
        startBitrateTestTCP();
        (await startSpeedTest)();
        dispatch({ type: 'next-pane' });
        break;
      default:
        dispatch({ type: 'next-pane' });
    }
  }, [
    startSipConnectivityTest,
    startCodecsTest,
    startBitrateTestUDP,
    startBitrateTestTCP,
    startSpeedTest,
    state,
    dispatch,
  ]);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => dispatch({ type: 'set-devices', devices }));
  }, []);

  return (
    <AppStateContext.Provider value={{ state, dispatch, nextPane, userAgentInfo, downloadFinalTestResults }}>
      {children}
    </AppStateContext.Provider>
  );
};
